import * as React from "react";
import clsx from "clsx";
import { Filter, Folder, Search } from "lucide-react";

import type {
  SnapshotCollection,
  SnapshotCollectionBatchJobsSummary,
} from "@/modules/snapshots/snapshotCollection.types";
import {
  getCollectionStatusMeta,
  type SnapshotCollectionTableRow,
} from "@/modules/snapshots/snapshotCollections/dashboardUtils";
import { formatRelativeDate } from "@/modules/snapshots/snapshotCollections/utils/dateFormatting";

const getBatchChipTitle = (summary: SnapshotCollectionBatchJobsSummary | null | undefined): string => {
  const base =
    "This collection runs through the provider Batch API (results can take up to 24h per dependent-prompt layer)";
  if (!summary) {
    return base;
  }
  const parts = [
    `${summary.jobs_in_flight} job${summary.jobs_in_flight === 1 ? "" : "s"} in flight`,
    `${summary.jobs_completed} completed`,
  ];
  if (summary.jobs_failed > 0) {
    parts.push(`${summary.jobs_failed} failed`);
  }
  if (summary.cells_pending > 0) {
    parts.push(`${summary.cells_pending} cells awaiting results`);
  }
  return `${base}. ${parts.join(", ")}.`;
};

interface Props {
  activeCollectionId?: string;
  query: string;
  rows: SnapshotCollectionTableRow[];
  onQueryChange: (query: string) => void;
  onOpenCollection: (collection: SnapshotCollection) => void;
}

export const SnapshotCollectionsTable: React.FC<Props> = ({
  activeCollectionId,
  query,
  rows,
  onQueryChange,
  onOpenCollection,
}) => {
  const emptyStateMessage = query.trim() ? "No collections match this search." : "No collections available.";

  return (
    <section className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb] shadow-none">
      <div className="flex min-w-0 flex-wrap items-center gap-4 border-b border-[#e4ddd5] px-5 py-4">
        <h2 className="min-w-0 flex-1 truncate text-lg font-bold text-[#07142d]">All collections</h2>

        <div className="flex w-full min-w-0 gap-2 sm:w-[320px] sm:flex-shrink-0">
          <label className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              value={query}
              onChange={event => onQueryChange(event.target.value)}
              aria-label="Search collections"
              name="snapshot-collections-table-search"
              placeholder="Search collections"
              autoComplete="off"
              className="h-10 w-full rounded-md border border-[#ddd5cb] bg-white pl-9 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-desert-sand-700 focus:ring-2 focus:ring-desert-sand-200"
              type="search"
            />
          </label>
          <button
            type="button"
            disabled
            className="inline-flex h-10 flex-shrink-0 items-center justify-center gap-2 rounded-md border border-[#ddd5cb] bg-white px-4 text-sm font-semibold text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Collection filters unavailable"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] table-fixed border-separate border-spacing-0">
          <colgroup>
            <col className="w-[34%]" />
            <col className="w-[30%]" />
            <col className="w-[18%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead className="bg-[#f7f2ec]">
            <tr>
              <TableHeader>Collection</TableHeader>
              <TableHeader>Template</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Last updated</TableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#ebe5de] bg-white">
            {rows.length > 0 ? (
              rows.map(row => (
                <CollectionRow
                  key={row.collection.id}
                  activeCollectionId={activeCollectionId}
                  row={row}
                  onOpenCollection={onOpenCollection}
                />
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-sm font-medium text-gray-500">
                  {emptyStateMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

const TableHeader: React.FC<TableHeaderProps> = ({ children, className }) => (
  <th
    scope="col"
    className={clsx(
      "h-10 min-w-0 px-5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500",
      className,
    )}
  >
    <span className="block truncate">{children}</span>
  </th>
);

interface CollectionRowProps {
  activeCollectionId?: string;
  row: SnapshotCollectionTableRow;
  onOpenCollection: (collection: SnapshotCollection) => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({ activeCollectionId, row, onOpenCollection }) => {
  const { collection, templateName } = row;
  const statusMeta = getCollectionStatusMeta(collection.status);
  const isActive = collection.id === activeCollectionId;

  return (
    <tr
      className={clsx("group h-[64px] transition-colors hover:bg-[#fbf8f4]", {
        "bg-[#fbf8f4]": isActive,
      })}
    >
      <td className="min-w-0 px-5 py-3 align-middle">
        <button
          type="button"
          onClick={() => onOpenCollection(collection)}
          className="flex max-w-full items-center gap-3 rounded-md text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-desert-sand-300"
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#f1e2ce] text-desert-sand-700">
            <Folder className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-gray-950">{collection.name}</span>
            <span className="mt-0.5 block truncate text-xs font-medium text-gray-500">No description</span>
          </span>
        </button>
      </td>
      <td className="min-w-0 px-5 py-3 align-middle">
        <span className="block truncate text-sm font-medium text-gray-700">{templateName}</span>
      </td>
      <td className="min-w-0 px-5 py-3 align-middle">
        <span
          className={clsx("inline-flex max-w-full items-center gap-2 text-sm font-semibold", statusMeta.textClassName)}
        >
          <span className={clsx("h-2 w-2 flex-shrink-0 rounded-full", statusMeta.dotClassName)} />
          <span className="truncate">{statusMeta.label}</span>
          {collection.status === "IN_PROGRESS" && typeof collection.progress === "number" && (
            <span className="flex-shrink-0 text-xs font-semibold text-gray-500">
              {Math.round(collection.progress * 100)}%
            </span>
          )}
          {collection.execution_mode === "batch" && (
            <span
              className="flex-shrink-0 rounded-full bg-desert-sand-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-desert-sand-700"
              title={getBatchChipTitle(collection.batch_jobs_summary)}
            >
              Batch
            </span>
          )}
        </span>
      </td>
      <td className="min-w-0 px-5 py-3 align-middle">
        <span className="block truncate text-sm font-medium text-gray-600">
          {formatRelativeDate(collection.created_at)}
        </span>
      </td>
    </tr>
  );
};
